import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog, faPencil, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Button, IconWithMenu, Image, Link, Text,
} from '@domain-group/suit-theme'

import type { OrderModel } from '@models/types'

import { confirmAction, confirmDelete } from '@functions/entity'

import useArtwork from '@hooks/useArtwork'
import useOrder from '@hooks/useOrder'

import OrderContainer from '../../../../components/OrderContainer'

import OrderItem from './OrderItem'

const defaultState = {
  updatingPaginationStatus: false,
}

interface PublicationOrderProps {
  order: OrderModel,
}

const PublicationOrder = (props: PublicationOrderProps) => {
  const { order } = props

  const [state, setState] = useSetState(defaultState)
  const { updatingPaginationStatus } = state

  const orderPayload = useOrder(order)
  const {
    callbacks: {
      createCreditNoteAndDeleteOrder,
      deleteOrder: deleteFn,
      editOrderTotal,
      toggleOrderPaginationApproval,
    },
    filteredOrderItems,
    firstArtwork,
    isArtworkApprovable,
    isBookable,
    isDeletable,
    nextDeadline,
    order: {
      id,
      pagination_approved,
      total,
    },
    processingFees,
    publication,
    totalEditable,
  } = orderPayload

  const artworkPayload = useArtwork(firstArtwork)
  const {
    callbacks: {
      approveArtwork: approveArtworkFn,
    },
  } = artworkPayload

  const logoUrl = digObject(publication, 'logo.thumb')

  const showAdditionalOptions = isDeletable

  return (
    <OrderContainer>
      <OrderContainer.Items>
        {filteredOrderItems.map(orderItem => (
          <OrderItem
            key={orderItem.id}
            orderItem={orderItem}
          />
        ))}
      </OrderContainer.Items>

      <OrderContainer.Footer>
        <Box alignItems="center">
          <Image
            backgroundSize="contain"
            borderRadius={5}
            boxProps={{ border: '1px solid', borderColor: 'lineColor' }}
            height={38}
            url={logoUrl}
            width={60}
          />

          <Box flexDirection="column" fontSize="xsmall" marginLeft="large">
            <Text color="lightGrey">Order #{id}</Text>
            {nextDeadline && (
              <Text color="bodyFontLightColor" marginTop="small">
                {nextDeadline.typeFormatted}: {nextDeadline.dateObject.toFormat("ccc dd LLL 'at' h:mma")}
              </Text>
            )}
          </Box>
        </Box>

        <Box alignItems="center" justifyContent="flex-end" marginLeft={[0, 'auto']} marginTop={['large', 0]}>
          <Box
            alignItems={['flex-start', 'flex-end']}
            flexDirection="column"
            flexShrink="0"
            justifyContent="center"
            marginRight={['auto', 0]}
            width="auto"
          >
            <Box alignItems="center">
              <Text fontSize="medium">
                {formatMoney(total)}
              </Text>

              {totalEditable && (
                <Link
                  onClick={editOrderTotal}
                  textProps={{ color: 'lightGrey', fontSize: 'xsmall', marginLeft: 'small' }}
                >
                  <FontAwesomeIcon icon={faPencil} />
                </Link>
              )}
            </Box>

            {processingFees > 0 && (
              <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small">
                + {formatMoney(processingFees)} Fees
              </Text>
            )}
          </Box>

          {(showAdditionalOptions || isArtworkApprovable || isBookable) && (
            <Box alignItems="center" marginLeft="large" width="auto">
              {showAdditionalOptions && (
                <IconWithMenu
                  boxProps={{ position: 'relative' }}
                  icon={(
                    <Button
                      buttonStyle="secondaryUtility"
                      icon={<FontAwesomeIcon icon={faCog} />}
                      size="medium"
                      style={{ height: 38 }}
                      width="auto"
                    />
                  )}
                >
                  {/*
                   // @ts-expect-error TS2741 */}
                  <IconWithMenu.Menu closeOnClick>
                    {isDeletable && (
                      <Link
                        key="delete"
                        onClick={() => createCreditNoteAndDeleteOrder({ deleteFn })}
                        // @ts-expect-error TS2322
                        tabIndex="0"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete Order
                      </Link>
                    )}
                  </IconWithMenu.Menu>
                </IconWithMenu>
              )}

              {isArtworkApprovable && (
                <Button
                  buttonStyle="primaryCreate"
                  onClick={() => confirmAction({
                    callbacks: {
                      actionFn: approveArtworkFn,
                    },
                    strings: {
                      actionString: 'approve',
                      entityName: 'artwork',
                    },
                  })}
                  marginLeft={4}
                  size="medium"
                  width="auto"
                >
                  Approve
                </Button>
              )}

              {isBookable && (
                <Button
                  buttonStyle={pagination_approved ? 'secondaryEdit' : 'primaryEdit'}
                  loading={updatingPaginationStatus}
                  loadingBubbleColor="white"
                  marginLeft={4}
                  onClick={() => {
                    setState({ updatingPaginationStatus: true })
                    toggleOrderPaginationApproval().then(() => {
                      setState({ updatingPaginationStatus: false })
                    })
                  }}
                  size="medium"
                  width="auto"
                >
                  {pagination_approved ? 'Pending' : 'Book'}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </OrderContainer.Footer>
    </OrderContainer>
  )
}

export default PublicationOrder
